import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import PeriodPicker from '../../components/venduto/PeriodPicker';
import { periodoRange, defaultSelezione } from '../../lib/venduto';
import { fmtSoldi, fmtPrezzo, fmtQta, fmtDataIt, ND } from '../../lib/acquisti';

const AcquistiFornitorePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useState(null);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    acquistiApi.getStato().then((s) => {
      setStato(s);
      setSel(defaultSelezione(s));
    }).catch(() => setCaricando(false));
  }, []);

  const range = sel ? periodoRange(sel, stato) : null;

  const carica = async (r) => {
    setCaricando(true);
    try {
      setDati(await acquistiApi.getFornitore(id, r.data_inizio, r.data_fine));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fornitore non trovato');
    } finally {
      setCaricando(false);
    }
  };

  useEffect(() => {
    if (range) carica(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.data_inizio, range?.data_fine, id]);

  const classifica = async (categoria) => {
    try {
      await acquistiApi.setCategoriaFornitore(id, categoria === NESSUNA ? null : categoria);
      toast.success('Categoria salvata e applicata allo storico');
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    }
  };

  const f = dati?.fornitore;
  const t = dati?.totali;
  const prec = dati?.confronto?.precedente;

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/acquisti/fornitori')} data-testid="fornitore-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-base font-bold leading-tight truncate" data-testid="fornitore-titolo">
              {f?.denominazione || 'Fornitore'}
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {f?.piva_cf}{f?.comune ? ` · ${f.comune}` : ''}
            </p>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {sel && <PeriodPicker value={sel} onChange={setSel} archivio={stato} testid="fornitore-periodo" />}

        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {t && (
          <>
            <Card data-testid="fornitore-riepilogo">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Acquistato nel periodo</p>
                  <p className="text-3xl font-bold tabular-nums" data-testid="fornitore-totale">{fmtSoldi(t.totale)}</p>
                  {prec?.disponibile ? (
                    <p className={`text-xs font-semibold flex items-center gap-1 ${prec.delta > 0 ? 'text-red-700' : 'text-emerald-600'}`}>
                      {prec.delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {prec.delta > 0 ? '+' : ''}{fmtSoldi(prec.delta)}
                      {prec.delta_pct !== null && ` (${prec.delta_pct > 0 ? '+' : ''}${prec.delta_pct}%)`}
                      <span className="text-muted-foreground font-normal">vs periodo precedente</span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Nessun acquisto nel periodo precedente</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div><p className="text-[11px] uppercase text-muted-foreground">Imponibile</p><p className="font-semibold tabular-nums">{fmtSoldi(t.imponibile)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">IVA</p><p className="font-semibold tabular-nums">{fmtSoldi(t.iva)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Fatture nel periodo</p><p className="font-semibold tabular-nums" data-testid="fornitore-n-fatture">{t.n_documenti}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Ultimo acquisto</p><p className="font-semibold">{fmtDataIt(f.ultimo_acquisto)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Totale storico</p><p className="font-semibold tabular-nums">{fmtSoldi(f.totale_acquistato)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Fatture storiche</p><p className="font-semibold tabular-nums">{f.n_documenti}</p></div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Categoria di costo</span>
                  {isAdmin ? (
                    <CategoriaSelect
                      valore={f.categoria_default}
                      categorie={stato?.categorie || []}
                      onChange={classifica}
                      testid="fornitore-categoria-select"
                      className="h-8 w-[180px]"
                    />
                  ) : (
                    <Badge variant={f.categoria_default ? 'secondary' : 'outline'} className="text-[10px]">
                      {f.categoria_default || 'DA CLASSIFICARE'}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {dati.andamento?.length > 1 && (
              <Card data-testid="fornitore-andamento">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Andamento della spesa</CardTitle>
                </CardHeader>
                <CardContent className="h-48 pr-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dati.andamento} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtSoldi(v)} />
                      <Bar dataKey="totale" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {dati.top_prodotti?.length > 0 && (
              <Card data-testid="fornitore-prodotti">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Cosa compri da questo fornitore</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dati.top_prodotti.map((p) => (
                    <div
                      key={p.prodotto_id}
                      className="flex items-center justify-between gap-3 py-2 border-b last:border-0 cursor-pointer"
                      onClick={() => navigate(`/acquisti/prodotti/${p.prodotto_id}`)}
                      data-testid={`fornitore-prodotto-${p.prodotto_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.descrizione || ND}</p>
                        <p className="text-xs text-muted-foreground">
                          {fmtQta(p.qta)} {p.um || ''} · ultimo {fmtPrezzo(p.ultimo_prezzo)}
                          {p.categoria ? ` · ${p.categoria}` : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0 flex items-center gap-1">
                        <span className="text-sm font-bold tabular-nums">{fmtSoldi(p.spesa)}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {dati.per_categoria?.length > 0 && (
              <Card data-testid="fornitore-categorie">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Spesa per categoria nel periodo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {dati.per_categoria.map((c) => (
                    <div key={c.categoria} className="flex items-center justify-between gap-2 text-sm">
                      <span className={c.categoria === 'DA CLASSIFICARE' ? 'text-amber-700 font-medium' : ''}>{c.categoria}</span>
                      <span className="font-semibold tabular-nums">{fmtSoldi(c.totale)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {dati.documenti?.length > 0 && (
              <Card data-testid="fornitore-documenti">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Fatture del periodo ({dati.documenti.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {dati.documenti.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 py-1.5 border-b last:border-0">
                      <div className="min-w-0">
                        <p className="text-sm">n. {d.numero || ND} · {fmtDataIt(d.data)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {d.n_righe} righe{d.segno === -1 ? ` · ${d.tipo_label}` : ''}
                        </p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums shrink-0">
                        {fmtSoldi((d.totale || 0) * (d.segno || 1))}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AcquistiFornitorePage;
